/**
 * Vehicle Database Editor
 * Section 6.1.2 - Search, view, edit vehicle records
 */

import { useState, useMemo } from 'react';
import oilSpecsData from '../../database/oil_specs.json';

function VehicleEditor() {
  const [searchFilters, setSearchFilters] = useState({
    year: '',
    make: '',
    model: '',
    engine: '',
    category: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const ITEMS_PER_PAGE = 50;

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    const years = [...new Set(oilSpecsData.map(s => s.year))].sort((a, b) => b - a);
    const makes = [...new Set(oilSpecsData.map(s => s.make))].sort();
    const categories = ['standard', 'european', 'truck'];
    return { years, makes, categories };
  }, []);

  // Filter data
  const filteredData = useMemo(() => {
    return oilSpecsData.filter(spec => {
      if (searchFilters.year && spec.year !== parseInt(searchFilters.year)) return false;
      if (searchFilters.make && spec.make !== searchFilters.make) return false;
      if (searchFilters.model && !spec.model.toLowerCase().includes(searchFilters.model.toLowerCase())) return false;
      if (searchFilters.engine && !spec.engine_code.toLowerCase().includes(searchFilters.engine.toLowerCase())) return false;
      if (searchFilters.category && spec.brand_category !== searchFilters.category) return false;
      return true;
    });
  }, [searchFilters]);

  // Paginate
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const handleFilterChange = (field, value) => {
    setSearchFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchFilters({ year: '', make: '', model: '', engine: '', category: '' });
    setCurrentPage(1);
  };

  const openEditModal = (record) => {
    setSelectedRecord({ ...record });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedRecord(null);
    setEditModalOpen(false);
  };

  return (
    <div className="vehicle-editor">
      <div className="admin-page-header">
        <h1>Vehicle Database</h1>
        <p>Search, view, and edit vehicle specifications ({oilSpecsData.length.toLocaleString()} records)</p>
      </div>

      {/* Search Filters */}
      <div className="admin-card">
        <h2>Search Filters</h2>
        <div className="filter-grid">
          <div className="admin-form-group">
            <label>Year</label>
            <select
              className="admin-select"
              value={searchFilters.year}
              onChange={(e) => handleFilterChange('year', e.target.value)}
            >
              <option value="">All Years</option>
              {filterOptions.years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="admin-form-group">
            <label>Make</label>
            <select
              className="admin-select"
              value={searchFilters.make}
              onChange={(e) => handleFilterChange('make', e.target.value)}
            >
              <option value="">All Makes</option>
              {filterOptions.makes.map(make => (
                <option key={make} value={make}>{make}</option>
              ))}
            </select>
          </div>

          <div className="admin-form-group">
            <label>Model</label>
            <input
              type="text"
              className="admin-input"
              value={searchFilters.model}
              onChange={(e) => handleFilterChange('model', e.target.value)}
              placeholder="Search model..."
            />
          </div>

          <div className="admin-form-group">
            <label>Engine</label>
            <input
              type="text"
              className="admin-input"
              value={searchFilters.engine}
              onChange={(e) => handleFilterChange('engine', e.target.value)}
              placeholder="Search engine..."
            />
          </div>

          <div className="admin-form-group">
            <label>Category</label>
            <select
              className="admin-select"
              value={searchFilters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">All Categories</option>
              {filterOptions.categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="admin-form-group filter-actions">
            <label>&nbsp;</label>
            <button className="admin-btn admin-btn-secondary" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        </div>

        <div className="filter-results">
          Showing {paginatedData.length} of {filteredData.length} results
        </div>
      </div>

      {/* Results Table */}
      <div className="admin-card">
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Make</th>
                <th>Model</th>
                <th>Engine</th>
                <th>Oil Type</th>
                <th>Capacity</th>
                <th>Category</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((spec, index) => (
                <tr key={`${spec.year}_${spec.make}_${spec.model}_${spec.engine_code}_${index}`}>
                  <td>{spec.year}</td>
                  <td>{spec.make}</td>
                  <td>{spec.model}</td>
                  <td><code>{spec.engine_code}</code></td>
                  <td>{spec.oil_type}</td>
                  <td>{spec.oil_capacity_qts} qt</td>
                  <td>
                    <span className={`category-badge ${spec.brand_category}`}>
                      {spec.brand_category}
                    </span>
                  </td>
                  <td>
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => openEditModal(spec)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="admin-btn admin-btn-secondary"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="admin-btn admin-btn-secondary"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModalOpen && selectedRecord && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="edit-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Vehicle Record</h2>
              <button className="modal-close" onClick={closeEditModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="edit-grid">
                <div className="admin-form-group">
                  <label>Year</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={selectedRecord.year}
                    onChange={(e) => setSelectedRecord(r => ({ ...r, year: parseInt(e.target.value) }))}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Make</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={selectedRecord.make}
                    onChange={(e) => setSelectedRecord(r => ({ ...r, make: e.target.value }))}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Model</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={selectedRecord.model}
                    onChange={(e) => setSelectedRecord(r => ({ ...r, model: e.target.value }))}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Engine Code</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={selectedRecord.engine_code}
                    onChange={(e) => setSelectedRecord(r => ({ ...r, engine_code: e.target.value }))}
                  />
                </div>
                <div className="admin-form-group full-width">
                  <label>Oil Type</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={selectedRecord.oil_type || ''}
                    onChange={(e) => setSelectedRecord(r => ({ ...r, oil_type: e.target.value }))}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Oil Capacity (qts)</label>
                  <input
                    type="number"
                    step="0.1"
                    className="admin-input"
                    value={selectedRecord.oil_capacity_qts || ''}
                    onChange={(e) => setSelectedRecord(r => ({ ...r, oil_capacity_qts: parseFloat(e.target.value) }))}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Drain Plug Torque (ft-lbs)</label>
                  <input
                    type="number"
                    className="admin-input"
                    value={selectedRecord.drain_plug_torque_ft_lbs || ''}
                    onChange={(e) => setSelectedRecord(r => ({ ...r, drain_plug_torque_ft_lbs: parseInt(e.target.value) }))}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Filter OEM</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={selectedRecord.filter_oem || ''}
                    onChange={(e) => setSelectedRecord(r => ({ ...r, filter_oem: e.target.value }))}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Category</label>
                  <select
                    className="admin-select"
                    value={selectedRecord.brand_category || 'standard'}
                    onChange={(e) => setSelectedRecord(r => ({ ...r, brand_category: e.target.value }))}
                  >
                    <option value="standard">Standard</option>
                    <option value="european">European</option>
                    <option value="truck">Truck</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Year Range</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={selectedRecord.year_range || ''}
                    onChange={(e) => setSelectedRecord(r => ({ ...r, year_range: e.target.value }))}
                    placeholder="e.g., 2015-2020"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="admin-btn admin-btn-secondary" onClick={closeEditModal}>
                Cancel
              </button>
              <button className="admin-btn admin-btn-primary" onClick={() => {
                alert('Save functionality requires backend API (coming in Phase 3)');
                closeEditModal();
              }}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .filter-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
        }

        .filter-actions {
          display: flex;
          flex-direction: column;
        }

        .filter-results {
          margin-top: 1rem;
          font-size: 0.875rem;
          color: #6b7280;
        }

        .table-container {
          overflow-x: auto;
        }

        .category-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
        }

        .category-badge.standard { background: #dbeafe; color: #1e40af; }
        .category-badge.european { background: #fef3c7; color: #92400e; }
        .category-badge.truck { background: #fee2e2; color: #991b1b; }

        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e5e7eb;
        }

        .page-info {
          font-size: 0.875rem;
          color: #6b7280;
        }

        code {
          background: #f3f4f6;
          padding: 0.125rem 0.375rem;
          border-radius: 4px;
          font-size: 0.8rem;
        }

        /* Modal styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .edit-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 700px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
        }

        .modal-body {
          padding: 1.5rem;
          overflow-y: auto;
        }

        .edit-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .edit-grid .full-width {
          grid-column: span 2;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }
      `}</style>
    </div>
  );
}

export default VehicleEditor;
